import { CrmFieldService, StandardIds, SystemFieldIds } from '@galaxy/crm/services/field-service';
import { Inject, Injectable } from '@angular/core';
import { ColumnDataInterface, TableCustomizationService } from './_abstract-table-customization.service';
import { firstValueFrom, Observable, of, combineLatest } from 'rxjs';
import { CrmObjectInterface, FieldSchemaInterface } from '@vendasta/crm';
import { OpportunityCustomColumns } from '../crm-services';
import { CrmDependencies, CrmInjectionToken, CRMFilterOverride } from '../../tokens-and-interfaces';
import { map, take } from 'rxjs/operators';
import { PAGE_ROUTES } from '../../constants';
import { CustomCell, GalaxyColumnDef } from '@vendasta/galaxy/table';
import {
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
} from '@vendasta/galaxy/filter/chips';
import { CrmObjectDisplayService } from '../crm-object-display.service';
import { TranslateService } from '@ngx-translate/core';
import {
  CrmOpportunityStageFilterInputComponent,
  CrmOpportunityStageFilterChipComponent,
} from '../../shared-components/custom-filters';

@Injectable()
export class OpportunityTableCustomizationService extends TableCustomizationService {
  private profileLink$: Observable<string>;

  constructor(
    protected override readonly crmFieldService: CrmFieldService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly objectDisplayService: CrmObjectDisplayService,
    private readonly translate: TranslateService,
  ) {
    super(crmFieldService);
    this.profileLink$ = this.config.routePrefix$.pipe(
      map((prefix) => {
        const viewRoute = `${PAGE_ROUTES.OPPORTUNITY.ROOT}/${PAGE_ROUTES.OPPORTUNITY.SUBROUTES.PROFILE}`;
        return prefix ? `${prefix}/${viewRoute}` : '';
      }),
    );
  }

  private hideIDColumns(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnIdentifiersToHide: string[] = [SystemFieldIds.OpportunityID, SystemFieldIds.OpportunityExternalID];

    for (const identifier of columnIdentifiersToHide) {
      const index = columns.findIndex((c) => c.id === identifier);
      if (index >= 0) {
        columns[index].hidden = true;
      }
    }

    for (const nameCol of columns.filter((c) => c.id === StandardIds.OpportunityName)) {
      nameCol.sticky = true;
    }

    return columns;
  }

  customizeColumnsMobile(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnIdentifiersToShow = new Map<string, boolean>([[StandardIds.OpportunityName, true]]);

    columns.forEach((column) => {
      column.hidden = !columnIdentifiersToShow.get(column.id);
    });
    return [...columns];
  }

  private addProfileLink(crmObject: CrmObjectInterface, columnData: ColumnDataInterface): void {
    const nameField = StandardIds.OpportunityName;
    this.profileLink$.pipe(take(1)).subscribe((profileLink) => {
      profileLink = profileLink.replace(':crmObjectId', crmObject.crmObjectId || '');
      columnData[nameField] = {
        cellType: 'text',
        value: this.getRowFullName(crmObject),
        link: profileLink,
      };
    });
  }

  async customizeRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): Promise<void> {
    this.addProfileLink(crmObject, columnData);
    await this.addPrimaryAssociationCustomCell(crmObject, columnData);
    this.addCustomCells(this.config.opportunity?.tableCustomCells || [], crmObject, columnData);
  }

  customizeColumns(columns: GalaxyColumnDef[], isMobile: boolean): GalaxyColumnDef[] {
    let customizedColumns = this.hideIDColumns(columns);
    customizedColumns = this.addPrimaryAssociationColumn(customizedColumns);
    const customCells = this.config.opportunity?.tableCustomCells || [];
    customizedColumns = this.addCustomColumns(
      customCells.map((c) => c.columnDefinition),
      customizedColumns,
    );
    if (isMobile) {
      customizedColumns = this.customizeColumnsMobile(customizedColumns);
    }
    return [...customizedColumns];
  }

  private addPrimaryAssociationColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: OpportunityCustomColumns.PrimaryAssociation,
        title: this.translate.instant('OPPORTUNITY.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.PRIMARY_ASSOCIATIONS'),
      },
      ...columns,
    ];
  }

  private async addPrimaryAssociationCustomCell(
    crmObject: CrmObjectInterface,
    columnData: ColumnDataInterface,
  ): Promise<void> {
    // Lazy import to break circular dependency
    const { PrimaryAssociationCellComponent } = await import(
      '../../shared-components/custom-table-cells/primary-association-cell/primary-association-cell.component'
    );

    columnData[OpportunityCustomColumns.PrimaryAssociation] = {
      cellType: 'custom',
      value: {
        objectId: crmObject.crmObjectId,
        objectType: 'Opportunity',
        showUrl: true,
      },
      component: PrimaryAssociationCellComponent,
    } as CustomCell;
  }

  getRowFullName(crmObject: CrmObjectInterface): string {
    return this.objectDisplayService.getObjectDisplayName('Opportunity', crmObject);
  }

  getAssociationInfo(crmObject: CrmObjectInterface): string {
    return this.objectDisplayService.getAssociationInfo('Opportunity', crmObject);
  }

  customizeFilters(filters: GalaxyFilterDefinitionInterface[]): Observable<GalaxyFilterDefinitionInterface[]> {
    const externalOverrides$ = this.config.opportunity?.filterInputOverrides$ ?? of([]);
    const appliedOverrides$ = externalOverrides$.pipe(
      map((externalOverrides) => {
        const defaultOverrides = this.getDefaultFilterOverrides();
        const allOverrides = [...defaultOverrides, ...externalOverrides];
        return this.overrideCustomFilters(filters, allOverrides);
      }),
    );
    return appliedOverrides$;
  }

  getDefaultFilterOverrides(): CRMFilterOverride[] {
    return [
      {
        fieldId: this.crmFieldService.getFieldId(StandardIds.OpportunityCalculatedStageID),
        title: this.translate.instant('OPPORTUNITY.LIST_OBJECTS_TABLE.FILTERS.STAGE'),
        filterInput: CrmOpportunityStageFilterInputComponent,
        filterChip: CrmOpportunityStageFilterChipComponent,
      },
    ];
  }

  buildInitialFilter(filterDefinitions: GalaxyFilterDefinitionInterface[], identifier: string, filterId: string) {
    const fieldId = this.crmFieldService.getFieldId(identifier);
    const defn = filterDefinitions.find((f) => f.fieldId === fieldId);
    if (defn) {
      defn.preset = true;
    }
    const filter = {
      fieldId: fieldId,
      filterId: filterId,
      operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
      values: [],
      definition: defn,
    };
    return filter;
  }

  async getInitialFilters(
    filterDefinitions$: Observable<GalaxyFilterDefinitionInterface[]>,
  ): Promise<GalaxyFilterInterface[]> {
    return firstValueFrom(combineLatest([filterDefinitions$, this.config.opportunity?.initialFilters$ || of([])])).then(
      ([filterDefinitions, initialFilters]) => {
        const filters = initialFilters.map((filter) => {
          return this.buildInitialFilter(filterDefinitions, filter.fieldId, filter.filterId);
        });
        return filters;
      },
    );
  }
}
